<script setup lang="ts">
import { toSizeName } from "core/helpers";
import { useField, useForm, useIsFormDirty, useIsFormValid } from "vee-validate";
import * as yup from "yup";

import { ALERT_STATUS } from "~/common/interfaces/alert-status.enum";
import type { UpdateSizeByIdDto } from "~/common/interfaces/dto/sizes/update-size-by-id.dto";
import { useAlertStore } from "~/stores/alert";
import { useModalStore } from "~/stores/modal";
import { useSizesStore } from "~/stores/sizes";

const { t } = useI18n();
const route = useRoute();
const alertStore = useAlertStore();
const sizesStore = useSizesStore();
const { sizesModalEdit } = useModalStore();
const { errors, handleSubmit } = useForm<UpdateSizeByIdDto>({
	initialValues: {
		width: sizesModalEdit.size.width || undefined,
		height: sizesModalEdit.size.height || undefined,
		length: sizesModalEdit.size.length || undefined
	},
	validationSchema: yup.object({
		width: yup.number().min(0, t("INPUT.width.error.gte")),
		height: yup.number().min(0, t("INPUT.height.error.gte")),
		length: yup.number().min(0, t("INPUT.length.error.gte"))
	})
});
const isFormDirty = useIsFormDirty();
const isFormValid = useIsFormValid();

const onSubmit = handleSubmit(async (dto: UpdateSizeByIdDto) => {
	sizesStore.currentSize = await sizesStore.patchById(sizesModalEdit.size.id, dto);

	sizesModalEdit.closeModal();

	if (route.name === "components-sizes") {
		sizesStore.getAll({}, sizesStore.recentGetAllQuery);
	}

	alertStore.addAlert({
		title: t("COMPONENTS.SIZES.MODAL_EDIT.alert.title"),
		description: t("COMPONENTS.SIZES.MODAL_EDIT.alert.description", { sizeName: toSizeName(sizesModalEdit.size) }),
		status: ALERT_STATUS.SUCCESS
	});
});

const { value: width } = useField<number>("width");
const { value: height } = useField<number>("height");
const { value: length } = useField<number>("length");
</script>

<template>
	<AppModal
		:title="$t('COMPONENTS.SIZES.MODAL_EDIT.title')"
		:close-button-text="$t('COMMON.cancel')"
		:submit-button-text="$t('COMMON.save')"
		:submit-button-active="isFormValid && isFormDirty"
		@close="sizesModalEdit.closeModal"
		@submit="onSubmit"
	>
		<span class="font-semibold text-base">
			{{ $t('COMMON.generalInfo') }}
		</span>
		<div class="mt-4 flex flex-col gap-y-2">
			<AppInput
				v-model="width"
				type="number"
				:title="$t('COMMON.width')"
				placeholder="0"
				min="0"
				step="0.01"
				:error="errors.width"
			/>
			<AppInput
				v-model="height"
				type="number"
				:title="$t('COMMON.height')"
				placeholder="0"
				min="0"
				step="0.01"
				:error="errors.height"
			/>
			<AppInput
				v-model="length"
				type="number"
				:title="$t('COMMON.length')"
				placeholder="0"
				min="0"
				step="0.01"
				:error="errors.length"
			/>
		</div>
	</AppModal>
</template>
